import { useState, useEffect, useRef, useMemo } from "react"
import { Link, useNavigate } from "react-router-dom"
import { FaEye } from "react-icons/fa";
import { toast } from 'react-toastify'
import Server from "../../Shared/Server"
import Spinner from '../../Shared/Spinner'

import {AgGridReact} from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const STATUSES = ['', 'Open', 'Assigned', 'In Progress', 'Resolved', 'Closed']
const PRIORITIES = ['', 'Low', 'Medium', 'High', 'Urgent']

const priorityBadge = {
    Low: 'secondary',
    Medium: 'warning',
    High: 'danger',
    Urgent: 'dark',
}

const statusBadge = {
    Open: 'primary',
    Assigned: 'info',
    'In Progress': 'warning',
    Resolved: 'success',
    Closed: 'secondary',
}

const MyComplaints = () => {
    const [complaints, setComplaints] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const gridRef = useRef()
    const navigate = useNavigate()

    const today = new Date().toISOString().split('T')[0]
    const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]

    const [filters, setFilters] = useState({
        _status: '',
        priority: '',
        fdate: firstOfMonth,
        tdate: today,
    })

    const fetchComplaints = async () => {
        setIsLoading(true)
        try {
            const params = new URLSearchParams()
            if (filters._status) params.append('_status', filters._status)
            if (filters.priority) params.append('priority', filters.priority)
            if (filters.fdate) params.append('fdate', filters.fdate)
            if (filters.tdate) params.append('tdate', filters.tdate)

            const response = await Server.get(`/complaints/my-complaints?${params.toString()}`)
            setComplaints(response?.data || [])
        } catch (error) {
            if (error?.response?.status === 401) {
                localStorage.removeItem('user')
                navigate('/login')
            } else if (error?.response?.status === 403) {
                toast.error(error?.response?.data?.detail || 'Forbidden')
                navigate('/')
            } else {
                toast.error(error?.response?.data?.detail || 'Failed to load complaints')
            }
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchComplaints()
    }, [])

    const onFilterChange = (e) => {
        setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }))
    }

    const onSearch = (e) => {
        e.preventDefault()
        fetchComplaints()
    }

    const defaultColDef = useMemo(() => ({ filterParams: { buttons: [] } }), [])

    const columnDefs = useMemo(() => [
        {
            headerName: "Actions",
            pinned: 'left',
            minWidth: 70,
            sortable: false,
            filter: false,
            cellRenderer: (e) => {
                if (!e.data) return null;
                return (
                    <Link className="btn btn-outline-info" to="/complaint-detail" state={{ complaint: e.data }} title="View complaint"><FaEye/></Link>
                )
            },
        },
        { headerName: "Customer", field: "customer_name", sortable: true, filter: true },
        { headerName: "Contact", field: "contact_number", sortable: true, filter: true },
        { headerName: "Village", field: "village_name", sortable: true, filter: true },
        { headerName: "Zone", field: "zone_name", sortable: true, filter: true },
        {
            headerName: "Priority",
            field: "priority",
            sortable: true,
            filter: true,
            cellRenderer: (e) => {
                if (!e.data) return null;
                const color = priorityBadge[e.data.priority] || 'secondary';
                return <span className={`badge bg-${color}`}>{e.data.priority}</span>
            }
        },
        {
            headerName: "Status",
            field: "status",
            sortable: true,
            filter: true,
            cellRenderer: (e) => {
                if (!e.data) return null;
                const color = statusBadge[e.data.status] || 'secondary';
                return <span className={`badge bg-${color}`}>{e.data.status}</span>
            }
        },
        { headerName: "Category", field: "category", sortable: true, filter: true },
        { headerName: "Response Type", field: "response_type", sortable: true, filter: true },
        { headerName: "Due Date", field: "due_date", sortable: true, filter: true },
        { headerName: "Created At", field: "created_at", sortable: true, filter: true },
    ], [])

    const onFirstDataRendered = (params) => { params.api.autoSizeAllColumns(); }
    const onGridSizeChanged = (params) => { params.api.autoSizeAllColumns(); }

    return (
        <>
            <div className="card">
                <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
                    <h3 className="card-title">My Complaints</h3>
                </div>
                <div className="card-body">
                    <form onSubmit={onSearch}>
                        <div className="row g-2 align-items-end">
                            <div className="col-md-2">
                                <label className="form-label">Status</label>
                                <select className="form-select" name="_status" value={filters._status} onChange={onFilterChange}>
                                    {STATUSES.map(s => <option key={s} value={s}>{s || 'All'}</option>)}
                                </select>
                            </div>
                            <div className="col-md-2">
                                <label className="form-label">Priority</label>
                                <select className="form-select" name="priority" value={filters.priority} onChange={onFilterChange}>
                                    {PRIORITIES.map(p => <option key={p} value={p}>{p || 'All'}</option>)}
                                </select>
                            </div>
                            <div className="col-md-2">
                                <label className="form-label">From</label>
                                <input type="date" className="form-control" name="fdate" value={filters.fdate} onChange={onFilterChange} max={today}/>
                            </div>
                            <div className="col-md-2">
                                <label className="form-label">To</label>
                                <input type="date" className="form-control" name="tdate" value={filters.tdate} onChange={onFilterChange}/>
                            </div>
                            <div className="col-md-1">
                                <button type="submit" className="btn btn-outline-info w-100" disabled={isLoading}>
                                    {isLoading ? '…' : 'Search'}
                                </button>
                            </div>
                        </div>
                    </form>

                    <div className="ag-theme-alpine responsive-aggrid mt-3" style={{ height: 500, width: '100%' }}>
                        <AgGridReact
                            suppressMenuHide={true}
                            onFirstDataRendered={onFirstDataRendered}
                            onGridSizeChanged={onGridSizeChanged}
                            defaultColDef={defaultColDef}
                            rowData={complaints}
                            domLayout={"normal"}
                            animateRows={true}
                            pagination={true}
                            paginationPageSize={50}
                            resizable={true}
                            enableCellTextSelection={true}
                            suppressCellFocus={true}
                            ref={gridRef}
                            columnDefs={columnDefs}
                        />
                    </div>
                </div>
            </div>
            <br/>
        </>
    )
}

export default MyComplaints
